import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  IconButton,
  Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { roleService, permissionService } from '../services/apiServices';
import ErrorDisplay from './ErrorDisplay';
import { sanitizeInput } from '../utils/validationUtils';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRole, setCurrentRole] = useState({
    id: null,
    name: '',
    permissions: []
  });
  const [availablePermissions, setAvailablePermissions] = useState([]);

  // Fetch roles and permissions
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch roles
        const rolesResponse = await roleService.getRoles();
        setRoles(rolesResponse.data);

        // Fetch permissions for the form
        const permissionsResponse = await permissionService.getPermissions();
        setAvailablePermissions(permissionsResponse.data);
      } catch (err) {
        let errorMessage = 'Error loading data. Please try again later.';
        
        if (err.response) {
          if (err.response.status === 401) {
            errorMessage = 'Session expired. Please log in again.';
            localStorage.removeItem('authToken');
            window.location.href = '/login';
          } else if (err.response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to view roles.';
          } else if (err.response.status === 500) {
            // Check if this is a specific access denied error
            if (err.response.data?.message?.includes('Access Denied')) {
              errorMessage = 'Access denied. You do not have permission to view roles. This feature requires role management permissions.';
            } else {
              errorMessage = 'Server error. Please try again later.';
            }
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.request) {
          errorMessage = 'Network error. Please check your connection.';
        } else {
          errorMessage = err.message || 'An unexpected error occurred.';
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleOpenDialog = (role = null) => {
    if (role) {
      // Editing existing role
      setIsEditing(true);
      setCurrentRole({
        id: role.id,
        name: role.name,
        permissions: role.permissions ? role.permissions.map(perm => perm.name) : []
      });
    } else {
      // Creating new role
      setIsEditing(false);
      setCurrentRole({
        id: null,
        name: '',
        permissions: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRole(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (event) => {
    const {
      target: { value },
    } = event;
    setCurrentRole(prev => ({
      ...prev,
      permissions: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        // Update existing role
        const response = await roleService.updateRole(currentRole.id, {
          permissions: currentRole.permissions
        });
        setRoles(prev => prev.map(r => r.id === currentRole.id ? response.data : r));
      } else {
        // Create new role
        const response = await roleService.createRole({
          name: currentRole.name,
          permissions: currentRole.permissions
        });
        setRoles(prev => [...prev, response.data]);
      }
      
      handleCloseDialog();
    } catch (err) {
      let errorMessage = 'Error saving role. Please try again.';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Session expired. Please log in again.';
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        } else if (err.response.status === 403) {
          errorMessage = 'Access denied. You do not have permission to modify roles.';
        } else if (err.response.status === 409) {
          errorMessage = err.response.data?.message || 'A role with this name already exists.';
        } else if (err.response.status === 500) {
          // Check if this is a specific access denied error
          if (err.response.data?.message?.includes('Access Denied')) {
            errorMessage = 'Access denied. You do not have permission to modify roles. This feature requires role management permissions.';
          } else {
            errorMessage = 'Server error. Please try again later.';
          }
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = err.message || 'An unexpected error occurred.';
      }
      
      setError(errorMessage);
    }
  };

  const handleDelete = async (roleId) => {
    if (window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      try {
        await roleService.deleteRole(roleId);
        setRoles(prev => prev.filter(r => r.id !== roleId));
      } catch (err) {
        let errorMessage = 'Error deleting role. Please try again.';
        
        if (err.response) {
          if (err.response.status === 401) {
            errorMessage = 'Session expired. Please log in again.';
            localStorage.removeItem('authToken');
            window.location.href = '/login';
          } else if (err.response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to delete roles.';
          } else if (err.response.status === 404) {
            errorMessage = 'Role not found.';
            // Remove role from local state if it was deleted on the server but not reflected here
            setRoles(prev => prev.filter(r => r.id !== roleId));
          } else if (err.response.status === 500) {
            // Check if this is a specific access denied error
            if (err.response.data?.message?.includes('Access Denied')) {
              errorMessage = 'Access denied. You do not have permission to delete roles. This feature requires role management permissions.';
            } else {
              errorMessage = 'Server error. Please try again later.';
            }
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.request) {
          errorMessage = 'Network error. Please check your connection.';
        } else {
          errorMessage = err.message || 'An unexpected error occurred.';
        }
        
        setError(errorMessage);
      }
    }
  };

  if (loading && roles.length === 0) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Roles Management
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <ErrorDisplay error={error} onClose={() => setError('')} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Roles Management
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Add Role
        </Button>
      </Box>
      
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>{sanitizeInput(role.id || 'N/A')}</TableCell>
                  <TableCell>{sanitizeInput(role.name || 'N/A')}</TableCell>
                  <TableCell>
                    {role.permissions && role.permissions.length > 0 
                      ? sanitizeInput(role.permissions.map(perm => perm.name).join(', ')) 
                      : 'No permissions'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit Role">
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleOpenDialog(role)}
                        aria-label="edit role"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Role">
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDelete(role.id)}
                        aria-label="delete role"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog for adding/editing roles */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit Role' : 'Add New Role'}
        </DialogTitle>
        <DialogContent>
          {!isEditing && (
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Role Name"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(currentRole.name)}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="permissions-label">Permissions</InputLabel>
            <Select
              labelId="permissions-label"
              id="permissions-select"
              multiple
              value={currentRole.permissions}
              onChange={handlePermissionChange}
              input={<OutlinedInput label="Permissions" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={sanitizeInput(value)} size="small" />
                  ))}
                </Box>
              )}
            >
              {availablePermissions.map((permission) => (
                <MenuItem key={permission.id} value={permission.name}>
                  <Checkbox checked={currentRole.permissions.indexOf(permission.name) > -1} />
                  <ListItemText primary={sanitizeInput(permission.name)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Roles;